import { FC, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import Modal from "../../../../../../components/modals/Modal";
import { CustomInput } from "../../../../../../components/common/Input/CustomInput";
import { DialogConfirmation } from "../../../../../../components/common/MiniDialog/DialogConfirmation";
import { colorVariables } from "../../../../../../utils/colorVariable";
import { useReviewAdjustment } from "../../../../hooks/useWarehouse";
import { adjustmentTypeLabel } from "../../../../utils/adjustmentTypes";
import { CanAccessFunctionality } from "../../../../../../components/common/Privilege/FunctionPrivilegeCheck";
import { E_FUNCTIONALITY } from "../../../../../../utils/functionalityVariable";

interface Props {
  open: boolean;
  onClose: () => void;
  row: any | null;
  category: "yarn" | "accessory";
}

type Action = "approve" | "reject" | "cancel";

const Info: FC<{ label: string; value?: any }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ fontWeight: "bold" }}>{label}</Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {value !== undefined && value !== null && value !== "" ? value : "-"}
    </Typography>
  </Box>
);

export const AdjustmentReviewModal: FC<Props> = ({ open, onClose, row, category }) => {
  const { mutate: review, isPending } = useReviewAdjustment(); // isLoading si react-query v4
  const [comment, setComment] = useState("");
  const [pendingAction, setPendingAction] = useState<Action | null>(null);

  useEffect(() => {
    if (open) {
      setComment("");
      setPendingAction(null);
    }
  }, [open, row?.id]);

  if (!row) return null;

  const unit = row.unit ?? (category === "yarn" ? "Kg" : "");
  const diff = Number(row.difference);
  const isPendingRow = row.status === "pending";
  const isLocation = row.adjustment_type === "location";

  const askConfirm = (action: Action) => {
    if ((action === "reject" || action === "cancel") && !comment.trim()) {
        return toast.error(
        action === "reject"
            ? "A comment is required to reject."
            : "A reason is required to cancel."
        );
    }
    setPendingAction(action);
    };

  const run = (action: Action) => {
    review(
      { id: row.id, action, comment },
      {
        onSuccess: () => {
          toast.success(
            action === "approve" ? "Adjustment approved."
              : action === "reject" ? "Adjustment rejected."
              : "Adjustment cancelled."
          );
          setPendingAction(null);
          onClose();
        },
        onError: (e) => {
          setPendingAction(null);
          toast.error(e.message);
        },
      }
    );
  };

  const actions: any[] = [{ text: "Close", onClick: onClose, variant: "secondary" }];
  if (isPendingRow) {
    if (
      CanAccessFunctionality({
        user_functionalities: [E_FUNCTIONALITY.btn_inventory_adjustement_reject],
      })
    ) {
      actions.push({
        text: "Reject",
        onClick: () => askConfirm("reject"),
        background: colorVariables.red,
        disabled: isPending,
      });
    }
    if (
      CanAccessFunctionality({
        user_functionalities: [E_FUNCTIONALITY.btn_inventory_adjustement_cancel],
      })
    ) {
      actions.push({
        text: "Cancel",
        onClick: () => askConfirm("cancel"),
        background: colorVariables.red,
        disabled: isPending,
      });
    }
    if (
      CanAccessFunctionality({
        user_functionalities: [E_FUNCTIONALITY.btn_inventory_adjustement_approve],
      })
    ) {
      actions.push({
        text: "Approve",
        onClick: () => askConfirm("approve"),
        background: colorVariables.gradient_primary,
        disabled: isPending,
      });
    }
  }

  const confirmQuestion =
    pendingAction === "approve" && isLocation
      ? `Are you sure you want to approve adjustment #${row.id}? The location will change to "${row.damage_location}".`
      : pendingAction === "approve"
      ? `Are you sure you want to approve adjustment #${row.id}? The stock will change from ${row.quantity_before} to ${row.new_quantity} ${unit}` +
        (row.adjustment_type === "damage"
          ? `, and ${Math.abs(diff)} ${unit} will be moved to "${row.damage_location}"`
          : "") +
        "."
      : pendingAction === "reject"
      ? `Are you sure you want to reject adjustment #${row.id}?`
      : `Are you sure you want to cancel your request #${row.id}?`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="750px"
      title={`Adjustment #${row.id} - ${row.stock_label ?? row.warehouse_id ?? ""}`}
      background={colorVariables.gradient_primary}
      actions={actions}
    >
      <Box display="flex" flexDirection="column" gap={2} width="100%">
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
          <Info label="Type" value={adjustmentTypeLabel(row.adjustment_type)} />
          <Info label="Status" value={row.status} />
          <Info label="Requested by" value={row.requested_by_name} />
          <Info label="Before" value={`${row.quantity_before} ${unit}`} />
          <Info label="New" value={`${row.new_quantity} ${unit}`} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: "bold" }}>Difference</Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: diff > 0 ? colorVariables.darkGreen : colorVariables.red }}
            >
              {diff > 0 ? "+" : ""}{diff} {unit}
            </Typography>
          </Box>
          <Info label="Date" value={dayjs(row.requested_at).format("DD/MM/YYYY HH:mm")} />
          {row.adjustment_type === "damage" && <Info label="Moved to" value={row.damage_location} />}
          {isLocation && <Info label="New location" value={row.damage_location} />}
        </Box>

        <Info label="Comment" value={row.reason_comment} />

        {row.adjustment_type === "damage" && isPendingRow && (
          <Typography variant="body2" color="text.secondary">
            On approval, {Math.abs(diff)} {unit} will be moved to <b>{row.damage_location}</b>.
          </Typography>
        )}

        {isPendingRow ? (
          <CustomInput
            label="Comment (required to reject or cancel)*"
            name="comment"
            width="100%"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
            <Info label="Reviewed by" value={row.reviewed_by_name} />
            <Info
              label="Reviewed at"
              value={row.reviewed_at ? dayjs(row.reviewed_at).format("DD/MM/YYYY HH:mm") : ""}
            />
            <Info label="Review comment" value={row.review_comment} />
          </Box>
        )}
      </Box>

      {pendingAction && (
        <DialogConfirmation
          color={pendingAction === "approve" ? colorVariables.orange : colorVariables.red}
          open={!!pendingAction}
          onClose={() => setPendingAction(null)}
          title={
            pendingAction === "approve" ? "Approve adjustment"
              : pendingAction === "reject" ? "Reject adjustment"
              : "Cancel adjustment"
          }
          fetchType={
            pendingAction === "approve" ? "Approval"
              : pendingAction === "reject" ? "Rejection"
              : "Cancel"
          }
          question={confirmQuestion}
          note={comment}
          onNoteChange={(e) => setComment(e.target.value)}
          isReasonOptional={pendingAction === "approve"}
          isLoading={isPending}
          onConfirm={() => {
            if (pendingAction !== "approve" && !comment.trim()) {
                return toast.error("A reason is required.");
            }
            run(pendingAction);
          }}
        />
      )}
    </Modal>
  );
};